import type { FeedbackGridView } from "@appretio/ui-kit";
import type { FeedbackPageContextGetter } from "./FeedbackContext";
import { GRID_VIEW_KEY } from "./feedbackViewRestore";

/** The grid half of a feedback restore: each keyed `DataTable` registers here. */
export interface GridViewRestore {
  /** Adds a keyed grid. Returns the function that removes it. */
  register: (key: string, grid: FeedbackGridView) => () => void;
  /**
   * Takes a report's parsed `context.grid` block (grid key → view), or null
   * when the report has none. Each view lands on its grid when the grid
   * registers, and again on each change: a column or filter can
   * appear only after the page's Apply. The view settles when it lands on a
   * grid that holds rows while no Apply is awaited, at the stage, the
   * register, a load or `settleWithoutApply`: whatever the grid still lacks
   * then is named in {@link notes}, and the view stops landing, so a later
   * load leaves the viewer's layout alone. A change the user makes to a grid
   * (a column, sort or filter) drops that grid's waiting view, without a
   * note. Other input on the page does not: an Apply the user presses still
   * lands the view.
   *
   * With `awaitApply`, no view settles until the handle's `applyDone` or
   * `settleWithoutApply`, so rows that render before the Apply do not settle
   * a view on the columns before it.
   *
   * Returns the release, which drops this stage's waiting views and notes;
   * releasing an older stage leaves a newer one alone.
   */
  stage: (
    stagedGrids: Record<string, unknown> | null,
    options?: { awaitApply?: boolean }
  ) => StagedGridViews;
  /** What a restore could not put back on the grids. Stable between changes. */
  notes: () => string[];
  /** Calls `listener` when the notes change. Returns the unsubscribe. */
  subscribe: (listener: () => void) => () => void;
}

/** The release of a {@link GridViewRestore.stage}, with its Apply signal. */
export interface StagedGridViews {
  (): void;
  /**
   * The restore's Apply is about to run, or user input ended the wait: each
   * view settles on its grid's next load, which can be the user's own Apply.
   */
  applyDone: () => void;
  /**
   * No Apply will come (the wait ran out, or the reporter's view is not
   * available): each view on a grid that holds rows settles now, on the rows
   * on screen. A grid without rows settles on its first load.
   */
  settleWithoutApply: () => void;
}

function quoted(colIds: string[]): string {
  return colIds.map((colId) => `"${colId}"`).join(", ");
}

/**
 * The grid-view restore of one `FeedbackProvider`. `registerPageContext` is
 * the provider's own, so a keyed grid's view rides reports.
 */
export function createGridViewRestore(
  registerPageContext: (getter: FeedbackPageContextGetter) => () => void
): GridViewRestore {
  const grids = new Map<string, FeedbackGridView>();
  const waiting = new Map<string, unknown>();
  const listeners = new Set<() => void>();
  let awaitingApply = false;
  let notes: string[] = [];
  // Which stage is current, so an older stage's release is a no-op.
  let stageId = 0;

  function setNotes(next: string[]) {
    notes = next;
    for (const listener of listeners) listener();
  }

  /**
   * Puts the waiting view on its grid. With `settles`, the view settles when
   * the grid holds rows and no Apply is awaited. A column load never settles
   * a view: the rows on the grid then can still be the ones before the Apply.
   */
  function land(key: string, settles: boolean) {
    const grid = grids.get(key);
    if (!grid || !waiting.has(key)) return;
    const view = waiting.get(key);
    const settled = settles && !awaitingApply && grid.hasRows();
    // Before the restore, so a load the restore fires does not land it again.
    if (settled) waiting.delete(key);
    const { missingColumns, missingFilters } = grid.restore(view);
    if (!settled) return;
    const next = [...notes];
    if (missingColumns.length > 0) {
      next.push(
        `The grid no longer has the reporter's columns ${quoted(missingColumns)}, so they are not shown.`
      );
    }
    if (missingFilters.length > 0) {
      next.push(
        `The reporter's filters on ${quoted(missingFilters)} were not restored, because the grid no longer offers them.`
      );
    }
    if (next.length !== notes.length) setNotes(next);
  }

  return {
    register(key, grid) {
      grids.set(key, grid);
      const unregisterContext = registerPageContext(() => ({
        [GRID_VIEW_KEY]: { [key]: grid.capture() }
      }));
      const unsubscribe = grid.subscribe((change) => {
        if (change === "user") waiting.delete(key);
        else land(key, change === "load");
      });
      land(key, true);
      return () => {
        unsubscribe();
        unregisterContext();
        if (grids.get(key) === grid) grids.delete(key);
      };
    },
    stage(stagedGrids, options) {
      stageId += 1;
      const id = stageId;
      waiting.clear();
      awaitingApply = options?.awaitApply ?? false;
      if (notes.length > 0) setNotes([]);
      for (const [key, view] of Object.entries(stagedGrids ?? {})) {
        waiting.set(key, view);
      }
      for (const key of [...waiting.keys()]) land(key, true);
      function release() {
        if (id !== stageId) return;
        waiting.clear();
        awaitingApply = false;
        if (notes.length > 0) setNotes([]);
      }
      function applyDone() {
        if (id === stageId) awaitingApply = false;
      }
      function settleWithoutApply() {
        // Only an awaited Apply holds views back: without one they settled
        // already, or user input left them for the user's own Apply.
        if (id !== stageId || !awaitingApply) return;
        awaitingApply = false;
        for (const key of [...waiting.keys()]) land(key, true);
      }
      return Object.assign(release, { applyDone, settleWithoutApply });
    },
    notes: () => notes,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    }
  };
}
